import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Stethoscope, RefreshCw, CircleCheck, WandSparkles, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { readPanel } from '../../lib/doctorCalls';
import { normPanel, panelError, fmtWhen, fmtAgo, rulesLine } from '../../lib/doctorView';
import { DOCTOR_TAG } from '../../lib/doctorSwitch';
import { tone, LevelIcon, DoctorFix, DismissFinding, HoldWhen, SettingChips, findingTone } from './DoctorParts';
import useChatsOwner from '../../hooks/useChatsOwner';
import { panelKey } from '../../lib/doctorKeys';

/*
 * r16sd (design 5.3): "Check this slot" - the doctor panel as a drawer, laid out like ReadinessDialog (what is wrong first, never a
 * wall of green). GET /uiapi/slots/{slot}/doctor is recomputed by the server from the slot's settings on every open, so it is the
 * freshest view: what holds a ticked feature back (holds), the settings that cannot work together (findings, with their fixes),
 * and what the session already handles by itself (auto-handled lines). Every sentence is the server's.
 *
 * Stage 5 (review SF1): a "set" fix is a one-click button (POST .../doctor/fix) and a WARN / INFO finding carries "Don't show
 * again" (POST .../doctor/dismiss); findings the server marks dismissed are listed under "Hidden".
 */

/** Review MF2: the panel's key carries the signed-in account (slot names like slot5 repeat across accounts; lib/doctorKeys.js). */
export const panelQueryKey = (owner, slotName) => panelKey(owner, slotName);

export function usePanel(slotName, enabled) {
  const owner = useChatsOwner();
  return useQuery({
    queryKey: panelQueryKey(owner, slotName),
    queryFn: () => readPanel(slotName).then((r) => r.data),
    enabled: !!(enabled && slotName),
    staleTime: 30000,
    retry: 0,
  });
}

function Section({ title, count, tone: t, children, testid }) {
  return (
    <section className="space-y-2" data-testid={testid}>
      <h3 className="flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {t && <span className={cn('h-1.5 w-1.5 rounded-full', t.dot)} aria-hidden />}
        {title}
        {Number.isFinite(count) && <span className="tnum text-muted-foreground/70">{count}</span>}
      </h3>
      <div className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border bg-card/60">{children}</div>
    </section>
  );
}

function Row({ level, text, meta, fixes, settings, slotName, deviceId, onFocusSetting, testid, muted, rule, applyBlocked, dismissRule }) {
  const t = tone(level);
  return (
    <div className="relative flex gap-3 px-4 py-3" data-testid={testid} data-level={level}>
      <span className={cn('absolute inset-y-0 left-0 w-[3px]', muted ? 'bg-border' : t.bar)} aria-hidden />
      {muted ? <WandSparkles className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden /> : <LevelIcon level={level} className="mt-0.5" />}
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className={cn('text-sm leading-relaxed text-pretty', muted ? 'text-muted-foreground' : 'text-foreground')}>{text}</p>
        {(meta || (settings && settings.length > 0)) && (
          <div className="flex flex-wrap items-center gap-2">
            {meta}
            <SettingChips settings={settings} />
          </div>
        )}
        {((fixes && fixes.length > 0) || dismissRule) && (
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            {(fixes || []).map((f) => (
              <DoctorFix key={f.id} fix={f} settings={settings} slotName={slotName} deviceId={deviceId} onFocusSetting={onFocusSetting}
                testid={`${testid}-fix-${f.id}`} rule={rule} nearText={text} applyBlocked={applyBlocked} />
            ))}
            {dismissRule && <DismissFinding rule={dismissRule} slotName={slotName} testid={`${testid}-dismiss`} />}
          </div>
        )}
      </div>
    </div>
  );
}

const FINDING_WORD = { BLOCK: 'Not saved', WARN: 'Check', INFO: 'Note' };

export function DoctorPanelBody({ panel, slotName, deviceId, onFocusSetting, applyBlocked }) {
  const [showHidden, setShowHidden] = useState(false);
  const p = panel;
  const auto = [...p.autoHandled, ...p.infoHolds.map((h) => ({ rule: h.rule, key: h.key, text: h.text, fixes: h.fix ? [h.fix] : [], hold: h }))];
  const nothing = !p.banners.length && !p.findings.length;
  return (
    <div className="space-y-5" data-testid="doctor-panel">
      <div className="flex flex-wrap gap-2" data-testid="doctor-summary">
        <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium tnum',
          p.banners.length ? tone(p.banners[0].level).chip : 'bg-secondary/70 text-muted-foreground ring-1 ring-border')}>
          {p.banners.length} holding it back
        </span>
        <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium tnum',
          p.findings.length ? tone(findingTone(p.findings[0].level)).chip : 'bg-secondary/70 text-muted-foreground ring-1 ring-border')}>
          {p.findings.length} to check
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary/70 px-2.5 py-1 text-xs font-medium text-muted-foreground ring-1 ring-border tnum">
          {auto.length} handled automatically
        </span>
      </div>

      {nothing && (
        <div className="flex items-start gap-3 rounded-xl border border-success/30 bg-success/10 px-4 py-3" data-testid="doctor-clear">
          <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
          <p className="text-sm text-foreground">Nothing to fix on this slot right now.</p>
        </div>
      )}

      {p.banners.length > 0 && (
        <Section title="Holding it back" count={p.banners.length} tone={tone(p.banners[0].level)} testid="doctor-holds">
          {p.banners.map((h, i) => (
            <Row key={`${h.rule}-${i}`} level={h.level} text={h.text} fixes={h.fix ? [h.fix] : []} settings={h.settings}
              meta={<HoldWhen hold={h} fmt={fmtWhen} />} slotName={slotName} deviceId={deviceId} onFocusSetting={onFocusSetting}
              testid={`doctor-hold-${h.rule}`} rule={h.rule} applyBlocked={applyBlocked} />
          ))}
        </Section>
      )}

      {p.findings.length > 0 && (
        <Section title="Settings to check" count={p.findings.length} tone={tone(findingTone(p.findings[0].level))} testid="doctor-findings">
          {p.findings.map((f, i) => (
            <Row key={`${f.rule}-${i}`} level={findingTone(f.level)} text={f.text} fixes={f.fixes} settings={f.settings}
              meta={f.level !== 'WARN' ? <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide', tone(findingTone(f.level)).chip)} data-testid={`doctor-finding-${f.rule}-level`}>{FINDING_WORD[f.level] || f.level}</span> : null}
              slotName={slotName} deviceId={deviceId} onFocusSetting={onFocusSetting} testid={`doctor-finding-${f.rule}`}
              rule={f.rule} applyBlocked={applyBlocked} dismissRule={f.level !== 'BLOCK' ? f.rule : null} />
          ))}
        </Section>
      )}

      {auto.length > 0 && (
        <Section title="Handled automatically" count={auto.length} testid="doctor-auto">
          {auto.map((a, i) => (
            <Row key={`${a.rule}-${i}`} level="info" muted text={a.text} fixes={a.fixes}
              meta={a.hold ? <HoldWhen hold={a.hold} fmt={fmtWhen} /> : null}
              slotName={slotName} deviceId={deviceId} onFocusSetting={onFocusSetting} testid={`doctor-auto-${a.rule}`}
              rule={a.rule} applyBlocked={applyBlocked} />
          ))}
        </Section>
      )}

      {p.dismissedFindings.length > 0 && (
        <div data-testid="doctor-hidden">
          <button type="button" onClick={() => setShowHidden((v) => !v)}
            className="tap inline-flex items-center gap-1.5 px-1 text-xs text-muted-foreground hover:text-foreground" aria-expanded={showHidden}>
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showHidden && 'rotate-180')} aria-hidden />
            Hidden until their settings change ({p.dismissedFindings.length})
          </button>
          {showHidden && (
            <div className="mt-2 divide-y divide-border/60 overflow-hidden rounded-xl border border-border bg-card/40">
              {p.dismissedFindings.map((f, i) => (
                <Row key={`${f.rule}-${i}`} level={findingTone(f.level)} muted text={f.text} settings={f.settings} slotName={slotName}
                  testid={`doctor-dismissed-${f.rule}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DoctorDrawer({ slotName, open, onOpenChange, deviceId, onFocusSetting, applyBlocked }) {
  const q = usePanel(slotName, open);
  const panel = q.data ? normPanel(q.data) : null;
  const err = q.error ? panelError(q.error) : null;

  useEffect(() => {
    if (!open || !panel) return;
    // eslint-disable-next-line no-console
    console.info(`${DOCTOR_TAG} panel ${slotName}: holds=${rulesLine(panel.banners)} findings=${rulesLine(panel.findings)}`
      + ` auto=${rulesLine(panel.autoHandled)} info=${rulesLine(panel.infoHolds)} hidden=${rulesLine(panel.dismissedFindings)}`);
    // one line per answer, not per render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, q.dataUpdatedAt]);

  useEffect(() => {
    if (!open || !err) return;
    // eslint-disable-next-line no-console
    console.info(`${DOCTOR_TAG} panel ${slotName}: not read (${err.kind}${err.status ? `, HTTP ${err.status}` : ''})`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, q.errorUpdatedAt]);

  const focus = onFocusSetting ? (id) => { onOpenChange(false); onFocusSetting(id); } : undefined;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 border-border bg-background p-0 sm:max-w-xl" data-testid="doctor-drawer">
        <SheetHeader className="space-y-1 border-b border-border/70 px-5 pb-4 pt-5 text-left">
          <SheetTitle className="flex min-w-0 items-center gap-2.5 pr-8 font-heading text-foreground">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/12 text-primary ring-1 ring-primary/20">
              <Stethoscope className="h-[18px] w-[18px]" aria-hidden />
            </span>
            <span className="min-w-0 truncate">Check {slotName}</span>
          </SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span>What holds this slot back, which settings clash, and what it already handles on its own.</span>
          </SheetDescription>
          <div className="flex items-center gap-2 pt-1 text-[11px] text-muted-foreground">
            {panel && Number.isFinite(panel.computedAt) && (
              <span className="tnum" title={fmtWhen(panel.computedAt)} data-testid="doctor-computed">Checked {fmtAgo(panel.computedAt)}</span>
            )}
            <button type="button" onClick={() => q.refetch()} disabled={q.isFetching}
              className="tap ml-auto inline-flex items-center gap-1 rounded-full px-2 py-1 hover:bg-secondary/70 hover:text-foreground"
              data-testid="doctor-refresh">
              <RefreshCw className={cn('h-3.5 w-3.5', q.isFetching && 'animate-spin text-primary')} aria-hidden /> Check again
            </button>
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {q.isLoading && (
            <div className="flex items-center gap-2 text-sm text-primary" aria-busy="true" data-testid="doctor-loading">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Checking...
            </div>
          )}
          {err && (
            <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-foreground" role="alert" data-testid={`doctor-error-${err.kind}`}>
              {err.kind === 'routeMissing' ? 'This check is not available yet.'
                : err.message || 'The check could not run just now. Try again in a minute.'}
            </div>
          )}
          {panel && !panel.doctor && (
            <p className="text-sm text-muted-foreground" data-testid="doctor-off">The check is switched off right now.</p>
          )}
          {panel && panel.doctor && (
            <DoctorPanelBody panel={panel} slotName={slotName} deviceId={deviceId} onFocusSetting={focus} applyBlocked={applyBlocked} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
